import React, { useState, useEffect, useRef } from 'react';
import { login as apiLogin, register as apiRegister, forgotPassword as apiForgotPassword, socialLogin as apiSocialLogin } from './api';
import { useAuth } from './AuthContext';

const GOOGLE_CLIENT_ID = '681277126180-qnbdru9tkmntgg6diu4nt1gbuconvji6.apps.googleusercontent.com';

const isNativePlatform = () => { try { return window.Capacitor && window.Capacitor.isNativePlatform(); } catch { return false; } };

const GoogleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" height="18" viewBox="0 0 18 18" width="18" style={{ flexShrink: 0 }}>
    <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908C16.658 14.013 17.64 11.706 17.64 9.2z" fill="#4285F4"/>
    <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
    <path d="M3.964 10.71c-.18-.54-.282-1.117-.282-1.71s.102-1.17.282-1.71V4.958H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
    <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335"/>
  </svg>
);

export default function AuthModal({ mode = 'register', onClose, onSuccess }) {
  const [tab, setTab] = useState(mode);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgot, setShowForgot] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);
  const [googleHover, setGoogleHover] = useState(false);
  const { login } = useAuth();
  const isNative = isNativePlatform();

  // Refs to avoid stale closures inside the async Google credential callback
  const loginRef = useRef(login);
  const onSuccessRef = useRef(onSuccess);
  useEffect(() => { loginRef.current = login; }, [login]);
  useEffect(() => { onSuccessRef.current = onSuccess; }, [onSuccess]);

  // Load Google GSI script and initialise (web only)
  useEffect(() => {
    if (isNative) return;

    const initGoogle = () => {
      if (!window.google?.accounts?.id) return;

      window.__mts_google_cb = async (response) => {
        setLoading(true);
        setError('');
        try {
          const res = await apiSocialLogin({ provider: 'google', token: response.credential });
          loginRef.current(res.data.token, res.data.user);
          onSuccessRef.current?.();
        } catch {
          setError('Google sign-in failed. Please try again or use email below.');
        } finally {
          setLoading(false);
        }
      };

      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (r) => window.__mts_google_cb?.(r),
        auto_select: false,
        cancel_on_tap_outside: true,
      });
    };

    if (window.google?.accounts?.id) {
      initGoogle();
    } else if (!document.getElementById('google-gsi-script')) {
      const script = document.createElement('script');
      script.id = 'google-gsi-script';
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    } else {
      const poll = setInterval(() => {
        if (window.google?.accounts?.id) { clearInterval(poll); initGoogle(); }
      }, 100);
      return () => clearInterval(poll);
    }

    return () => { delete window.__mts_google_cb; };
  }, [isNative]);

  const handleGoogleClick = () => {
    if (!window.google?.accounts?.id) {
      setError('Google sign-in is not available. Please use email below.');
      return;
    }
    setError('');
    setLoading(true);
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
        setLoading(false);
        setError('Google sign-in was blocked by your browser. Try in a regular window or use email below.');
      }
    });
  };

  const handleSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      let res;
      if (tab === 'register') {
        if (!form.name || !form.email || !form.password) { setError('Please fill in all fields.'); setLoading(false); return; }
        if (form.password.length < 6) { setError('Password must be at least 6 characters.'); setLoading(false); return; }
        res = await apiRegister(form);
        // Fire FB Pixel Lead event on successful new user signup
        if (typeof window !== 'undefined' && window.fbq) {
          window.fbq('track', 'Lead');
        }
      } else {
        if (!form.email || !form.password) { setError('Please fill in all fields.'); setLoading(false); return; }
        res = await apiLogin({ email: form.email, password: form.password });
      }
      login(res.data.token, res.data.user);
      onSuccess?.();
    } catch (e) {
      setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async () => {
    if (!forgotEmail.trim()) return;
    setForgotLoading(true);
    try {
      await apiForgotPassword(forgotEmail.trim());
    } catch {
      // Swallow errors — always show the same message
    } finally {
      setForgotLoading(false);
      setForgotSent(true);
    }
  };

  const handleTabChange = (t) => {
    setTab(t);
    setShowForgot(false);
    setForgotSent(false);
    setError('');
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose?.()}>
      <div className="modal">
        <div style={{ position: 'relative', textAlign: 'center', marginBottom: 24 }}>
          {!showForgot && tab === 'login' && (
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28 }}>Welcome Back</h2>
          )}
          {!showForgot && tab === 'register' && (
            <div style={{ background: 'var(--gold-10)', border: '1px solid var(--gold-20)', borderRadius: 8, padding: '14px 16px', fontSize: 15, textAlign: 'center' }}>
              🎁 <strong style={{ color: 'var(--gold)' }}>7 Free Scripts</strong> — Create an account to view and save your script
            </div>
          )}
          {showForgot && (
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 28 }}>Reset Password</h2>
          )}
          <button onClick={onClose} style={{ position: 'absolute', top: -8, right: -8, background: 'rgba(255,255,255,0.08)', border: 'none', color: 'var(--white-60)', fontSize: 18, cursor: 'pointer', padding: 0, width: 36, height: 36, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {/* Forgot password view */}
        {showForgot ? (
          <div>
            {forgotSent ? (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <div style={{ fontSize: 40, marginBottom: 16 }}>📬</div>
                <p style={{ fontSize: 15, color: 'var(--white-80)', lineHeight: 1.7, marginBottom: 24 }}>
                  If an account exists with that email, you'll receive a reset link shortly.
                </p>
                <button onClick={() => { setShowForgot(false); setForgotSent(false); }}
                  style={{ background: 'none', border: 'none', color: 'var(--gold)', fontSize: 14, cursor: 'pointer' }}>
                  ← Back to Sign In
                </button>
              </div>
            ) : (
              <>
                <p style={{ color: 'var(--white-60)', fontSize: 14, marginBottom: 20 }}>
                  Enter your email and we'll send you a link to reset your password.
                </p>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ fontSize: 13, color: 'var(--white-60)', marginBottom: 6, display: 'block' }}>Email</label>
                  <input type="email" placeholder="your@email.com" value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleForgot()} />
                </div>
                <button className="btn-primary" onClick={handleForgot}
                  disabled={forgotLoading || !forgotEmail.trim()} style={{ marginBottom: 12 }}>
                  {forgotLoading ? 'Sending...' : 'Send Reset Link'}
                </button>
                <button onClick={() => setShowForgot(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--white-60)', fontSize: 14, cursor: 'pointer', width: '100%', textAlign: 'center' }}>
                  ← Back to Sign In
                </button>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Tab switcher */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 24, background: 'var(--white-10)', borderRadius: 8, padding: 4 }}>
              {['register', 'login'].map(t => (
                <button key={t} onClick={() => handleTabChange(t)} style={{
                  flex: 1, padding: '10px', borderRadius: 6, fontSize: 14, fontWeight: 600,
                  background: tab === t ? 'var(--gold)' : 'transparent',
                  color: tab === t ? 'var(--navy)' : 'var(--white-60)',
                  border: 'none', cursor: 'pointer', transition: 'all 0.2s'
                }}>
                  {t === 'register' ? 'Create Account' : 'Sign In'}
                </button>
              ))}
            </div>

            {/* Google button — web only */}
            {!isNative && (
              <div style={{ marginBottom: 20 }}>
                <button
                  onClick={handleGoogleClick}
                  disabled={loading}
                  onMouseEnter={() => setGoogleHover(true)}
                  onMouseLeave={() => setGoogleHover(false)}
                  style={{
                    width: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                    padding: '12px 16px',
                    background: googleHover ? '#f8f8f8' : '#ffffff',
                    border: '1px solid #dadce0',
                    borderRadius: 8,
                    color: '#3c4043',
                    fontSize: 15,
                    fontWeight: 500,
                    fontFamily: 'inherit',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.7 : 1,
                    transition: 'background 0.15s',
                    boxSizing: 'border-box',
                  }}>
                  <GoogleIcon />
                  Continue with Google
                </button>

                {/* Divider */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '18px 0 4px' }}>
                  <div style={{ flex: 1, height: 1, background: 'var(--white-10)' }} />
                  <span style={{ fontSize: 13, color: 'var(--white-40)', whiteSpace: 'nowrap' }}>or</span>
                  <div style={{ flex: 1, height: 1, background: 'var(--white-10)' }} />
                </div>
              </div>
            )}

            {/* Email / password form */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {tab === 'register' && (
                <div>
                  <label style={{ fontSize: 13, color: 'var(--white-60)', marginBottom: 6, display: 'block' }}>Your Name</label>
                  <input placeholder="What is your first name?" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                </div>
              )}
              <div>
                <label style={{ fontSize: 13, color: 'var(--white-60)', marginBottom: 6, display: 'block' }}>Email</label>
                <input type="email" placeholder="your@email.com" value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
              </div>
              <div>
                <label style={{ fontSize: 13, color: 'var(--white-60)', marginBottom: 6, display: 'block' }}>Password</label>
                <input type="password" placeholder="At least 6 characters" value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
                {tab === 'login' && (
                  <button onClick={() => { setShowForgot(true); setForgotEmail(form.email); }}
                    style={{ background: 'none', border: 'none', color: 'var(--white-60)', fontSize: 13, cursor: 'pointer', marginTop: 6, padding: 0, textDecoration: 'underline' }}>
                    Forgot your password?
                  </button>
                )}
              </div>
            </div>

            {error && <p style={{ color: 'var(--error)', fontSize: 14, marginTop: 12 }}>{error}</p>}

            <button className="btn-primary" onClick={handleSubmit} disabled={loading} style={{ marginTop: 20 }}>
              {loading ? 'Please wait...' : tab === 'register' ? 'Create Account & View Script' : 'Sign In'}
            </button>

            {tab === 'register' && (
              <p style={{ fontSize: 12, color: 'var(--white-60)', marginTop: 12, textAlign: 'center' }}>
                By creating an account you agree to our Terms of Service and Privacy Policy.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
